// Tipos para el sistema de personalización de agendas

export type CoverStyle = 'premium' | 'deluxe' | 'signature';

export type EngravingPosition = 'superior' | 'centro' | 'inferior' | 'esquina';

export interface DesignOption {
  id: string;
  name: string;
  imageUrl?: string;
  category?: string;
}

export interface TypographyOption {
  id: string;
  name: string;
  fontFamily: string;
}

export interface ColorOption {
  id: string;
  name: string;
  hex: string;
}

export interface AgendaCustomizationState {
  // Estilo de cubierta
  coverStyle: CoverStyle;

  // Diseño seleccionado
  designId?: string;
  customDesignFile?: File;

  // Grabado (opcional)
  hasEngraving: boolean;
  engravingText?: string;
  engravingPosition?: EngravingPosition;
  engravingTypography?: string;
  engravingColor?: string;
  hasLogo?: boolean;
  logoFile?: File;

  // Opciones adicionales
  hasDeluxeSeparators?: boolean;
  foilOptions?: string[];
}

export interface CustomizationPricing {
  basePrice: number;
  deluxeSeparatorsPrice: number;
  foilPrice: number;
  logoVectorizationPrice: number;
  engravingPrice: number;
}

export const DEFAULT_PRICING: CustomizationPricing = {
  basePrice: 0,
  deluxeSeparatorsPrice: 170,
  foilPrice: 105,
  logoVectorizationPrice: 50,
  engravingPrice: 0, // Incluido en la personalización
};

export const COVER_STYLES: { value: CoverStyle; label: string; imageUrl?: string }[] = [
  { value: 'premium', label: 'Premium', imageUrl: '/images/covers/premium.jpg' },
  { value: 'deluxe', label: 'Deluxe', imageUrl: '/images/covers/deluxe.jpg' },
  { value: 'signature', label: 'Signature', imageUrl: '/images/covers/signature.jpg' },
];

export const DESIGN_OPTIONS: DesignOption[] = [
  { id: 'confetti', name: 'Confetti' },
  { id: 'contrast', name: 'Contrast' },
  { id: 'cottage-in-yellow', name: 'Cottage in Yellow' },
  { id: 'cottage', name: 'Cottage' },
  { id: 'cute', name: 'Cute' },
  { id: 'abstract', name: 'Abstract' },
  { id: 'almond', name: 'Almond' },
  { id: 'anime', name: 'Anime' },
  { id: 'antique', name: 'Antique' },
  { id: 'big-ideas', name: 'Big Ideas' },
  { id: 'blush', name: 'Blush' },
  { id: 'books', name: 'Books' },
  { id: 'bouquet', name: 'Bouquet' },
  { id: 'canvas', name: 'Canvas' },
  { id: 'caramel', name: 'Caramel' },
  { id: 'cheetah', name: 'Cheetah' },
  { id: 'cherry-blossom', name: 'Cherry Blossom' },
  { id: 'cherry', name: 'Cherry' },
  { id: 'chic', name: 'Chic' },
  { id: 'clouds', name: 'Clouds' },
  { id: 'concrete', name: 'Concrete' },
  { id: 'frida', name: 'Frida' },
  { id: 'garden', name: 'Garden' },
  { id: 'giverny', name: 'Giverny' },
  { id: 'grid-in-blue', name: 'Grid in Blue' },
  { id: 'grid-in-lilac', name: 'Grid in Lilac' },
  { id: 'grid-in-pink', name: 'Grid in Pink' },
  { id: 'groove', name: 'Groove' },
  { id: 'happy', name: 'Happy' },
  { id: 'hearts', name: 'Hearts' },
  { id: 'dandelion', name: 'Dandelion' },
  { id: 'delicate', name: 'Delicate' },
  { id: 'dental-in-black', name: 'Dental in Black' },
  { id: 'dental-in-pink', name: 'Dental in Pink' },
  { id: 'doll', name: 'Doll' },
  { id: 'dream', name: 'Dream' },
  { id: 'edge', name: 'Edge' },
  { id: 'elegance-2', name: 'Elegance 2' },
  { id: 'elegance-in-lilac', name: 'Elegance in Lilac' },
  { id: 'elegance', name: 'Elegance' },
  { id: 'flower', name: 'Flower' },
  { id: 'notes', name: 'Notes' },
  { id: 'ombre', name: 'Ombre' },
  { id: 'over-the-rainbow', name: 'Over the Rainbow' },
  { id: 'picnic', name: 'Picnic' },
  { id: 'pink-haze', name: 'Pink Haze' },
  { id: 'planter', name: 'Planter' },
  { id: 'polka', name: 'Polka' },
  { id: 'pretty', name: 'Pretty' },
  { id: 'remembrance', name: 'Remembrance' },
  { id: 'romance', name: 'Romance' },
  { id: 'honey', name: 'Honey' },
  { id: 'inspire', name: 'Inspire' },
  { id: 'lavender', name: 'Lavender' },
  { id: 'law', name: 'Law' },
  { id: 'marble', name: 'Marble' },
  { id: 'mundus', name: 'Mundus' },
  { id: 'nen-fares', name: 'Nenúfares' },
  { id: 'starry', name: 'Starry' },
  { id: 'striped', name: 'Striped' },
  { id: 'stripes', name: 'Stripes' },
  { id: 'study', name: 'Study' },
  { id: 'teach', name: 'Teach' },
  { id: 'tulips-II', name: 'Tulips II' },
  { id: 'tulips', name: 'Tulips' },
  { id: 'valley', name: 'Valley' },
  { id: 'waterlilies', name: 'Waterlilies' },
  { id: 'waves', name: 'Waves' },
  { id: 'wheat', name: 'Wheat' },
  { id: 'wish', name: 'Wish' },
  { id: 'year', name: 'Year' },
  { id: 'school', name: 'School' },
  { id: 'shower', name: 'Shower' },
  { id: 'simply', name: 'Simply' },
  { id: 'heart', name: 'Heart' },
  { id: 'lake', name: 'Lake' },
  { id: 'neutral-bouquet', name: 'Neutral Bouquet' },
  { id: 'night-2', name: 'Night 2' },
  { id: 'night', name: 'Night' },
  { id: 'parade', name: 'Parade' },
  { id: 'petals', name: 'Petals' },
  { id: 'possible', name: 'Possible' },
  { id: 'pretty-bouquet', name: 'Pretty Bouquet' },
  { id: 'aroma', name: 'Aroma' },
  { id: 'beach', name: 'Beach' },
  { id: 'bloomish', name: 'Bloomish' },
  { id: 'campagne', name: 'Campagne' },
  { id: 'circle', name: 'Circle' },
  { id: 'day', name: 'Day' },
  { id: 'dental', name: 'Dental' },
  { id: 'dusty-bouquet', name: 'Dusty Bouquet' },
  { id: 'endless', name: 'Endless' },
  { id: 'fairytale', name: 'Fairytale' },
];

export const TYPOGRAPHY_OPTIONS: TypographyOption[] = [
  { id: 'serif', name: 'Serif', fontFamily: 'Georgia, serif' },
  { id: 'classic', name: 'Classic', fontFamily: 'Times New Roman, serif' },
  { id: 'elegant', name: 'Elegant', fontFamily: 'Playfair Display, serif' },
  { id: 'montserrat', name: 'Montserrat', fontFamily: 'Montserrat, sans-serif' },
  { id: 'baskerville', name: 'Baskerville', fontFamily: 'Baskerville, serif' },
  { id: 'emily', name: 'Emily', fontFamily: 'Dancing Script, cursive' },
  { id: 'milonga', name: 'Milonga', fontFamily: 'Milonga, cursive' },
  { id: 'delius', name: 'Delius', fontFamily: 'Delius, cursive' },
  { id: 'lexend', name: 'Lexend', fontFamily: 'Lexend, sans-serif' },
  { id: 'playwrite', name: 'Playwrite', fontFamily: 'Pacifico, cursive' },
  { id: 'italic', name: 'Italic', fontFamily: 'Georgia, serif' },
  { id: 'spectral', name: 'Spectral', fontFamily: 'Spectral, serif' },
];

export const ENGRAVING_COLORS: ColorOption[] = [
  { id: 'rose', name: 'Rose', hex: '#FFB6C1' },
  { id: 'blush', name: 'Blush', hex: '#FFC0CB' },
  { id: 'berry', name: 'Berry', hex: '#DC143C' },
  { id: 'grape', name: 'Grape', hex: '#6F2DA8' },
  { id: 'lavender', name: 'Lavender', hex: '#E6E6FA' },
  { id: 'nude', name: 'Nude', hex: '#E3BC9A' },
  { id: 'wine', name: 'Wine', hex: '#722F37' },
  { id: 'sky', name: 'Sky', hex: '#87CEEB' },
  { id: 'teal', name: 'Teal', hex: '#008080' },
  { id: 'navy', name: 'Navy', hex: '#000080' },
  { id: 'olive', name: 'Olive', hex: '#808000' },
  { id: 'gold', name: 'Gold', hex: '#FFD700' },
  { id: 'silver', name: 'Silver', hex: '#C0C0C0' },
  { id: 'onix', name: 'Onix', hex: '#0F0F0F' },
  { id: 'snow', name: 'Snow', hex: '#FFFAFA' },
  { id: 'rose-gold', name: 'Rose Gold', hex: '#B76E79' },
];

export const VINIPIEL_COLORS: ColorOption[] = [
  { id: 'rose-blush', name: 'Rose Blush', hex: '#FFB6C1' },
  { id: 'berry', name: 'Berry', hex: '#DC143C' },
  { id: 'grape', name: 'Grape', hex: '#6F2DA8' },
  { id: 'lavender', name: 'Lavender', hex: '#E6E6FA' },
  { id: 'nude', name: 'Nude', hex: '#E3BC9A' },
  { id: 'honey', name: 'Honey', hex: '#FDB813' },
  { id: 'wine', name: 'Wine', hex: '#722F37' },
  { id: 'sky', name: 'Sky', hex: '#87CEEB' },
  { id: 'teal', name: 'Teal', hex: '#008080' },
  { id: 'royal', name: 'Royal', hex: '#4169E1' },
  { id: 'navy', name: 'Navy', hex: '#000080' },
  { id: 'fire', name: 'Fire', hex: '#FF4500' },
  { id: 'olive', name: 'Olive', hex: '#808000' },
  { id: 'gray', name: 'Gray', hex: '#808080' },
  { id: 'smoke', name: 'Smoke', hex: '#738276' },
  { id: 'marble', name: 'Marble', hex: '#F5F5DC' },
];

export const ARGOLLA_COLORS: ColorOption[] = [
  { id: 'silver', name: 'Silver', hex: '#C0C0C0' },
  { id: 'onix', name: 'Onix', hex: '#0F0F0F' },
  { id: 'snow', name: 'Snow', hex: '#FFFAFA' },
  { id: 'rose', name: 'Rose', hex: '#FFB6C1' },
];

export const MAX_ENGRAVING_LENGTH = 30;

export const ENGRAVING_POSITIONS: { value: EngravingPosition; label: string }[] = [
  { value: 'superior', label: 'Superior' },
  { value: 'centro', label: 'Centro' },
  { value: 'inferior', label: 'Inferior' },
  { value: 'esquina', label: 'Esquina' },
];
